import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { invalidateCache } from '@/shared/lib/useCachedQuery';
import { updateConversationSettings } from '../api/userApi';
import type { CurrentUser, LearningLanguage } from './types';
import { loadCurrentUser } from './useCurrentUser';

/**
 * Как язык называется в переключателе — на самом языке, а не на языке
 * интерфейса: так его узнают в списке и те, кто читает интерфейс по-русски.
 * Флаг — `LanguageFlag`, национальный (у португальского — Португалии, не
 * Бразилии, по решению Дмитрия), хотя учим именно бразильский вариант —
 * голоса и промпт тьютора завязаны на него, флаг это не отражает.
 */
export const LEARNING_LANGUAGE_META: Record<LearningLanguage, { name: string }> = {
  en: { name: 'English' },
  de: { name: 'Deutsch' },
  pt: { name: 'Português' },
};

/**
 * Порядок, в котором язык предлагают выбрать (шаг онбординга). Ключи меты, а
 * не второй список рядом: добавить язык = дописать его в мету одним местом.
 * До `/me` этот список тоже доступен — в онбординге выбор идёт раньше, чем
 * сервер успевает сказать `learningLanguages`.
 */
export const LEARNING_LANGUAGES = Object.keys(LEARNING_LANGUAGE_META) as LearningLanguage[];

interface LearningLanguageContextValue {
  /** Null — `/me` ещё не ответил или ответил ошибкой. */
  language: LearningLanguage | null;
  languages: readonly LearningLanguage[];
  /**
   * Растёт на каждую смену языка. Ключ для перемонтирования экранов (см.
   * `MainLayout`): все загрузки живут в эффектах на монтировании, и
   * пересоздать экран — единственный способ перечитать их все разом, не
   * расставляя язык в зависимости каждого хука.
   */
  generation: number;
  isSwitching: boolean;
  switchLanguage: (next: LearningLanguage) => Promise<void>;
}

const LearningLanguageContext = createContext<LearningLanguageContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
  /** Профиль пришёл — приложение заодно синхронизирует язык интерфейса (см. `App`). */
  onUserLoaded?: (user: CurrentUser) => void;
}

/**
 * Активный изучаемый язык на всё приложение. Хранится на сервере
 * (`users.learning_language`) и оттуда же читается: сервер сам фильтрует по
 * нему статистику, ошибки, историю и практику, клиенту достаточно знать его
 * для шапки и для того, что есть только у английского.
 */
export function LearningLanguageProvider({ children, onUserLoaded }: ProviderProps) {
  const [language, setLanguage] = useState<LearningLanguage | null>(null);
  const [languages, setLanguages] = useState<readonly LearningLanguage[]>([]);
  const [generation, setGeneration] = useState(0);
  const [isSwitching, setSwitching] = useState(false);

  useEffect(() => {
    let active = true;
    // Через общий кеш `/me` — тот же запрос, что ждут `RootRedirect` и главная.
    loadCurrentUser()
      .then((user) => {
        if (!active) return;
        setLanguage(user.learningLanguage);
        setLanguages(user.learningLanguages);
        onUserLoaded?.(user);
      })
      .catch(() => {
        // Не удалось — шапка без переключателя, остальное работает как английский.
      });
    return () => {
      active = false;
    };
    // onUserLoaded — стрелка вызывающего, нужен только первый ответ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchLanguage = useCallback(
    async (next: LearningLanguage) => {
      if (next === language || isSwitching) return;
      setSwitching(true);
      try {
        await updateConversationSettings({ learningLanguage: next });
        // Весь кеш — чужого языка: статистика, ошибки, история, практика,
        // настройки разговора (уровень и темп у языка свои).
        invalidateCache();
        setLanguage(next);
        setGeneration((value) => value + 1);
      } finally {
        setSwitching(false);
      }
    },
    [language, isSwitching],
  );

  const value = useMemo(
    () => ({ language, languages, generation, isSwitching, switchLanguage }),
    [language, languages, generation, isSwitching, switchLanguage],
  );

  return <LearningLanguageContext.Provider value={value}>{children}</LearningLanguageContext.Provider>;
}

export function useLearningLanguage(): LearningLanguageContextValue {
  const value = useContext(LearningLanguageContext);
  if (!value) throw new Error('useLearningLanguage вне LearningLanguageProvider');
  return value;
}
